import React, { useState, useEffect } from 'react';
import { updateConsentMode, triggerConsentEvent } from 'utils/consentUtils';
import { COOKIE_CONSENT_CAPTURED_AT_COOKIE } from 'utils/getCookieConsent';
import { CookiesConsentType } from 'types/CookiesConsentType';
import { CookieConsentContext } from './CookieConsentContext';
import Cookies from 'js-cookie';

export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [cookieBannerChecked, toggleCookieBannerChecked] =
    useState<boolean>(false);
  const [cookiesConsent, setCookiesConsent] = useState<CookiesConsentType>({
    essential: true,
    functional: false,
    analytics: false,
    marketing: false,
    social: false,
  });

  useEffect(() => {
    const storedConsent = Cookies.get('cookieConsent');
    if (storedConsent) {
      try {
        const parsedConsent = JSON.parse(storedConsent);
        setCookiesConsent(parsedConsent);
        updateConsentMode(parsedConsent);
        // Legacy decisions predate the companion timestamp - backfill it once
        // so every subsequent event carries a STABLE capturedAt instead of a
        // fresh event-time value on each event.
        if (!Cookies.get(COOKIE_CONSENT_CAPTURED_AT_COOKIE)) {
          Cookies.set(
            COOKIE_CONSENT_CAPTURED_AT_COOKIE,
            new Date().toISOString(),
            { expires: 365 }
          );
        }
      } catch (error) {
        console.error('Error parsing cookie consent:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (cookieBannerChecked) {
      Cookies.set('cookieConsent', JSON.stringify(cookiesConsent), {
        expires: 365,
      });
      // Companion cookie, NOT a key inside the consent JSON: TermsAndPrivacy
      // renders a checkbox per key of that JSON, so a foreign key there would
      // draw a bogus checkbox. Re-stamped on every write so it always
      // reflects the latest decision.
      Cookies.set(COOKIE_CONSENT_CAPTURED_AT_COOKIE, new Date().toISOString(), {
        expires: 365,
      });
      updateConsentMode(cookiesConsent);
      triggerConsentEvent();
    }
  }, [cookiesConsent, cookieBannerChecked]);

  return (
    <CookieConsentContext.Provider
      value={{
        cookiesConsent,
        setCookiesConsent,
        cookieBannerChecked,
        toggleCookieBannerChecked,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};
